import type { SessionStatus } from './enums.js';
import { elapsedSeconds } from './datetime.js';

/**
 * Session lifecycle.
 *
 * An explicit transition table rather than scattered `if` checks, because the
 * phone and the PC both drive these transitions and a session that reached an
 * impossible state would corrupt history in a way that is very hard to unpick.
 *
 *     active ⇄ paused
 *        ↓        ↓
 *     completed / abandoned
 *
 * `completed` is terminal. Corrections to a finished workout are made through
 * the audited history-edit path, not by reopening the session.
 *
 * `abandoned → active` **is** permitted: a session auto-abandoned after a long
 * gap can be resumed, and the logged sets were never destroyed.
 */

const TRANSITIONS: Readonly<Record<SessionStatus, readonly SessionStatus[]>> = Object.freeze({
  active: ['paused', 'completed', 'abandoned'],
  paused: ['active', 'completed', 'abandoned'],
  completed: [],
  abandoned: ['active', 'completed'],
});

export const TERMINAL_STATUSES: readonly SessionStatus[] = ['completed'];

export function isTerminal(status: SessionStatus): boolean {
  return TERMINAL_STATUSES.includes(status);
}

/** Whether a session is open for logging. */
export function isOpen(status: SessionStatus): boolean {
  return status === 'active' || status === 'paused';
}

export function canTransition(from: SessionStatus, to: SessionStatus): boolean {
  if (from === to) return true; // Idempotent: re-applying a state is harmless.
  return TRANSITIONS[from].includes(to);
}

export function allowedTransitions(from: SessionStatus): readonly SessionStatus[] {
  return TRANSITIONS[from];
}

export class InvalidSessionTransitionError extends Error {
  constructor(
    readonly from: SessionStatus,
    readonly to: SessionStatus,
  ) {
    super(
      `Cannot move a session from '${from}' to '${to}'. Allowed: ${
        TRANSITIONS[from].length > 0 ? TRANSITIONS[from].join(', ') : '(none — terminal)'
      }`,
    );
    this.name = 'InvalidSessionTransitionError';
  }
}

export interface TransitionOutcome {
  status: SessionStatus;
  /** Set when the transition ends the session. */
  endedAt: number | null;
  changed: boolean;
}

/**
 * Apply a transition, or throw.
 *
 * Throwing rather than returning a flag is deliberate: an invalid transition is
 * a programming error, not a user outcome, and silently ignoring it would leave
 * the UI and the database disagreeing about what state a workout is in.
 */
export function transitionSession(
  from: SessionStatus,
  to: SessionStatus,
  at: number,
): TransitionOutcome {
  if (!canTransition(from, to)) {
    throw new InvalidSessionTransitionError(from, to);
  }

  const endsSession = to === 'completed' || to === 'abandoned';
  return {
    status: to,
    endedAt: endsSession ? at : null,
    changed: from !== to,
  };
}

export interface AbandonmentCheck {
  shouldPrompt: boolean;
  shouldAutoAbandon: boolean;
  openForSeconds: number;
  hoursOpen: number;
}

/**
 * Whether an unfinished session has been open too long (requirement I1).
 *
 * Returns advice, never an action. Logged sets are never discarded — an
 * abandoned session keeps everything that was recorded.
 */
export function evaluateAbandonment(options: {
  status: SessionStatus;
  startedAt: number;
  now: number;
  /** Hours after which the session is considered abandoned. Default 12. */
  autoAbandonAfterHours?: number;
}): AbandonmentCheck {
  const { status, startedAt, now, autoAbandonAfterHours = 12 } = options;

  if (!isOpen(status)) {
    return { shouldPrompt: false, shouldAutoAbandon: false, openForSeconds: 0, hoursOpen: 0 };
  }

  const openForSeconds = elapsedSeconds(startedAt, now);
  const hoursOpen = openForSeconds / 3600;

  return {
    // Any reopened unfinished session prompts; only a stale one auto-abandons.
    shouldPrompt: true,
    shouldAutoAbandon: hoursOpen >= autoAbandonAfterHours,
    openForSeconds,
    hoursOpen,
  };
}

/**
 * Whether a session carries data worth keeping.
 *
 * Used to decide whether an abandoned session should still appear in history.
 * A session with logged sets always is — that is the data the user actually
 * produced, and the whole point of never deleting it.
 */
export function hasRecoverableData(options: {
  completedSetCount: number;
  loggedSetCount: number;
}): boolean {
  return options.completedSetCount > 0 || options.loggedSetCount > 0;
}
